import React, { useEffect, useState, useContext } from "react";
import {
  TextField,
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
} from "@mui/material";
import CreateIcon from "@mui/icons-material/Create";

import Swal from "sweetalert2";
import Toast from "../../utils/Toast";

import DataTable from "react-data-table-component";

import useGetStripeFees from "../../queries/stripeFees/getStripeFees";
import useCreateAudit from "../../mutation/audit/createAudit";
import { AuthContext } from "../../utils/userProvider";

import Loader from "../../components/Loader";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
  modalSx,
  DeleteModalFrame,
} from "../../components/ListPage";

const schema = yup.object().shape({
  flatFee: yup
    .number()
    .typeError("AU Flat Fee must be a number")
    .required("AU Flat Fee is required")
    .min(0, "AU Flat Fee cannot be negative"),
  flatFeeHk: yup
    .number()
    .typeError("HK Flat Fee must be a number")
    .nullable()
    .min(0, "HK Flat Fee cannot be negative"),
  percentage: yup
    .number()
    .typeError("AU Percentage must be a number")
    .required("AU Percentage is required")
    .min(0, "AU Percentage cannot be negative"),
  percentageHk: yup
    .number()
    .typeError("HK Percentage must be a number")
    .nullable()
    .min(0, "HK Percentage cannot be negative"),
});

const StripeFee = () => {
  const [feeTable, setFeeTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);

  const [openEditStripeFee, setOpenEditStripeFee] = useState(false);
  const [auditResponse, setAuditData] = useCreateAudit();
  const [editRow, setEditRow] = useState();

  const [currentData, setCurrentData] = useState();
  const [snapshotCurrent, setSnapshotCurrent] = useState({});

  const { control, register, handleSubmit, setValue, errors } = useForm({
    resolver: yupResolver(schema),
  });

  let columns = [
    {
      name: "Name",
      selector: "name",
      sortable: true,
    },
    {
      name: "AU Flat Fee",
      selector: "flatFee",
      sortable: true,
      cell: (row, index, column, id) => {
        return <div>AUD {row.flatFee.toFixed(2)}</div>;
      },
    },
    {
      name: "HK Flat Fee",
      selector: "flatFeeHk",
      sortable: true,
      cell: (row, index, column, id) => {
        const val = row.flatFeeHk;
        const hasValue =
          val != null && val !== "" && !Number.isNaN(parseFloat(val));
        return (
          <div>{hasValue ? `HKD ${parseFloat(val).toFixed(2)}` : "-"}</div>
        );
      },
    },
    {
      name: "AU % of Total Payable",
      selector: "percentage",
      sortable: true,
      cell: (row, index, column, id) => {
        return <div>{row.percentage.toFixed(2)}%</div>;
      },
    },
    {
      name: "HK % of Total Payable",
      selector: "percentageHk",
      sortable: true,
      cell: (row, index, column, id) => {
        const val = row.percentageHk;
        const hasValue =
          val != null && val !== "" && !Number.isNaN(parseFloat(val));
        return (
          <div>{hasValue ? `${parseFloat(val).toFixed(2)}%` : "-"}</div>
        );
      },
    },
  ];
  if (authState.role == "Executive") {
    columns.push({
      name: "Options",
      sortable: false,
      cell: (row, index, column, id) => {
        if (!row.archived) {
          return (
            <div>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CreateIcon />}
                onClick={() => {
                  editItem(row);
                }}
              >
                Edit
              </Button>
            </div>
          );
        } else {
          return null;
        }
      },
    });
  }

  const [stripeFeeResponse, resetStripeFee] = useGetStripeFees();

  const updateTableData = (data) => {
    let newTableData = [...feeTable];
    if (data != null) {
      let index = newTableData.findIndex((object) => object.id == data.id);
      newTableData[index] = data;
      setFeeTable(newTableData);
    } else {
      setFeeTable(newTableData);
    }
  };

  useEffect(() => {
    if (stripeFeeResponse.data) {
      setFeeTable([...stripeFeeResponse.data]);
    }
  }, [stripeFeeResponse]);

  var countDecimals = function (value) {
    if (Math.floor(value) === value) return 0;
    if (value.toString().split(".").length > 1) {
      return value.toString().split(".")[1].length || 0;
    } else {
      return 0;
    }
  };

  const editItem = (row) => {
    setEditRow(row);
    setCurrentData(row.id);
    setSnapshotCurrent(row);
    setValue("percentage", row.percentage);
    setValue("percentageHk", row.percentageHk ?? "");
    setValue("flatFee", row.flatFee);
    setValue("flatFeeHk", row.flatFeeHk ?? "");
    setOpenEditStripeFee(true);
  };

  const handleClose = () => {
    setOpenEditStripeFee(false);
  };

  const updateTextField = (event, parama) => {
    let value = event.target.value;
    if (countDecimals(value) > 2) {
      setValue(parama, Number(value).toFixed(2));
    }
  };

  const onSubmit = (values) => {
    if (values) {
      let submitData = { ...values };
      submitData.id = editRow.id;
      submitData.flatFeeHk =
        submitData.flatFeeHk !== "" && submitData.flatFeeHk != null
          ? parseFloat(submitData.flatFeeHk)
          : null;
      submitData.percentageHk =
        submitData.percentageHk !== "" && submitData.percentageHk != null
          ? parseFloat(submitData.percentageHk)
          : null;
      Swal.fire({
        title: "Create Updates",
        html: `<h4>Please provide a description before submitting your changes</h4>
        <textarea maxlength="100" type="text" id="actionDescription" class="swal2-input" placeholder="Description" font-size=18px style="height:auto!important" rows=5>`,
        confirmButtonText: "Confirm",
        focusConfirm: false,
        preConfirm: () => {
          const actionDescription =
            Swal.getPopup().querySelector("#actionDescription").value;
          if (!actionDescription) {
            Swal.showValidationMessage(`Please enter Description`);
          }
          return {
            actionDescription: actionDescription,
            newData: submitData,
            snapshotCurrent: snapshotCurrent,
            currentData: currentData,
            type: "stripe-fee",
            action: "update",
          };
        },
      }).then((result) => {
        if (result.isConfirmed) {
          setAuditData(result.value);
        }
      });
    }
  };

  useEffect(() => {
    try {
      if (auditResponse.data) {
        Toast.fire({
          icon: "success",
          title: "Stripe Fees update Audit Created",
        });
        setOpenEditStripeFee(false);
      } else if (auditResponse.error) {
        let data = auditResponse.errorMessage.response.data;
        console.log(data);
        if (data) {
          let messageData = data.message;
          let type = messageData.message;
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.errorMessage.message,
          });
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: auditResponse.errorMessage.message,
          });
        }
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    }
  }, [auditResponse]);

  return (
    <ListPageShell>
      <ListPageCard>
        {stripeFeeResponse.loading && <Loader />}
        {!stripeFeeResponse.loading && (
          <div>
            <ListPageHeader title="Stripe Fees" />
            <DataTable
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10]}
              noHeader={true}
              subHeader
              data={feeTable}
              columns={columns}
              customStyles={dataTableCustomStyles}
            />
          </div>
        )}
      </ListPageCard>
      <Modal
        aria-labelledby="edit-modal"
        aria-describedby="edit-modal-description"
        open={openEditStripeFee}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose();
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEnforceFocus
        sx={modalSx}
      >
        <Fade in={openEditStripeFee}>
          <Box>
            {editRow && (
              <DeleteModalFrame title={`Edit ${editRow.name}`}>
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit(onSubmit)}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <TextField
                    name="flatFee"
                    margin="normal"
                    fullWidth
                    label="AU Flat Fee (AUD)*"
                    variant="outlined"
                    type="number"
                    inputRef={register}
                    onChange={(e) => {
                      updateTextField(e, "flatFee");
                    }}
                    defaultValue={editRow ? editRow.flatFee : null}
                    error={errors.flatFee != null}
                    helperText={
                      errors.flatFee != null ? errors.flatFee.message : null
                    }
                    sx={{ width: 380 }}
                  />
                  <TextField
                    name="flatFeeHk"
                    margin="normal"
                    fullWidth
                    label="HK Flat Fee (HKD)"
                    variant="outlined"
                    type="number"
                    inputRef={register}
                    onChange={(e) => {
                      updateTextField(e, "flatFeeHk");
                    }}
                    defaultValue={editRow ? editRow.flatFeeHk ?? "" : ""}
                    error={errors.flatFeeHk != null}
                    helperText={
                      errors.flatFeeHk != null
                        ? errors.flatFeeHk.message
                        : "Optional - used for HK property transactions"
                    }
                    sx={{ width: 380 }}
                  />
                  <TextField
                    name="percentage"
                    margin="normal"
                    fullWidth
                    type="number"
                    label="AU % of Total Payable*"
                    variant="outlined"
                    inputRef={register}
                    onChange={(e) => {
                      updateTextField(e, "percentage");
                    }}
                    defaultValue={editRow ? editRow.percentage : null}
                    error={errors.percentage != null}
                    helperText={
                      errors.percentage != null
                        ? errors.percentage.message
                        : "Used for Australian property transactions"
                    }
                    sx={{ width: 380 }}
                  />
                  <TextField
                    name="percentageHk"
                    margin="normal"
                    fullWidth
                    type="number"
                    label="HK % of Total Payable"
                    variant="outlined"
                    inputRef={register}
                    onChange={(e) => {
                      updateTextField(e, "percentageHk");
                    }}
                    defaultValue={editRow ? editRow.percentageHk ?? "" : ""}
                    error={errors.percentageHk != null}
                    helperText={
                      errors.percentageHk != null
                        ? errors.percentageHk.message
                        : "Optional - used for HK property transactions"
                    }
                    sx={{ width: 380 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 5,
                      gap: 1,
                    }}
                  >
                    <Button type="submit" variant="contained" color="primary">
                      Confirm
                    </Button>
                    <Button
                      onClick={handleClose}
                      variant="contained"
                      color="secondary"
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </DeleteModalFrame>
            )}
          </Box>
        </Fade>
      </Modal>
    </ListPageShell>
  );
};

export default StripeFee;
